import React from "react";
import { Center, Column, Row } from "@/shared/ui/Blocks";
import { Icon } from "@/shared/ui/Icon";
import { MemberCard, MemberCardWrapper } from "../MemberCard";
import { Person } from "@/features/people/graphql/generated";
import useOrgNavigate from "@/shared/utils/router/useOrgNavigate";
import { cn } from "@/shared/utils/shadcn";
import { every, get } from "lodash";
import { useTranslation } from "react-i18next";
import { AssessmentSurveyType } from "@/shared/constants/assessmentSurveys";
import { getIsSurveysCompleted } from "@/shared/utils/selectors/getSurveyCompletion";

interface MembersRowProps {
  teamId: number;
  members: Person[];
  exclude?: number[];
  canAdd?: boolean;
  canRemove?: boolean;
  dimensionsConfig?: AssessmentSurveyType[];
  onMemberAddClick?: () => void;
  onMemberRemoveClick?: (member: Person) => void;
  className?: string;
}

const getMemberStatus = (
  member: Person,
  dimensionsConfig?: AssessmentSurveyType[]
) => {
  if (member?.invite) {
    return { label: "Pending", variant: "neutralFilled" as const };
  }

  const isComplete = getIsSurveysCompleted(
    member.completed_dim_types as AssessmentSurveyType[],
    dimensionsConfig
  );

  if (!isComplete) {
    return { label: "Incomplete", variant: "warningFilled" as const };
  }

  return undefined;
};

function MembersRow(props: MembersRowProps) {
  const navigate = useOrgNavigate();
  const {
    members,
    teamId,
    exclude,
    canAdd,
    canRemove,
    dimensionsConfig,
    onMemberAddClick,
    onMemberRemoveClick,
    className,
  } = props;

  const navigateMember = (memberId: number) => () => {
    navigate(`/teams/${teamId}/member/${memberId}`);
  };

  const visibleMembers = exclude?.length
    ? members.filter((m) => !exclude.includes(m.id!))
    : members;

  const { t } = useTranslation();

  const getMemberDisabledDescription = (member: Person) => {
    if (member.invite?.secret) {
      return t("profile.not_registered_user_description");
    }

    const isComplete = getIsSurveysCompleted(
      member.completed_dim_types as AssessmentSurveyType[],
      dimensionsConfig
    );

    if (!isComplete) {
      return t("profile.incomplete_profile_description");
    }
    return undefined;
  };

  return (
    <Row className={cn("gap-4 flex-wrap relative", className)}>
      {canAdd && (
        <MemberCardWrapper
          id="add-member-card"
          onClick={onMemberAddClick}
          className="border-primary border-dashed items-center justify-center"
        >
          <Column className="h-full items-center justify-center gap-1 bg-blue-100/60">
            <Center className="p-3 bg-white rounded-full aspect-square ">
              <Icon size="sm" icon="AddUser" color="primary" />
            </Center>
            <p className="text-primary font-semibold">Add</p>
          </Column>
        </MemberCardWrapper>
      )}
      {visibleMembers?.map((member) => (
        <MemberCard
          className="teamMemberSingle"
          key={member?.id}
          name={member?.name || member?.email || ""}
          initials={member?.initials || ""}
          avatarUrl={member?.avatar || ""}
          onClick={navigateMember(member?.id!)}
          onRemove={canRemove ? () => onMemberRemoveClick?.(member) : undefined}
          status={getMemberStatus(member, dimensionsConfig)}
          disabled={!!member.invite?.secret}
          disabledDescription={getMemberDisabledDescription(member)}
        />
      ))}
    </Row>
  );
}

export { MembersRow };
